import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { links } from '@eis/core';
import { PropertyFilter, RequirePermission, type PropertyFilterValue } from '@eis/patterns';
import { andFilters } from '@eis/core';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/leads';
import { kindCounts } from '../model/leads';
import { ImportLeadsWizard } from './ImportLeadsWizard';
import { LeadFormDialog } from './LeadFormDialog';
import { LeadGrid } from './LeadGrid';

/**
 * Leads tab of a Gruppen-Police (SDD-013 §8.1.2, R-2): `ListLeads(parent_policy_code)` — the
 * same grid as the standalone list minus the parent column, a kind summary in the header, and the
 * two actions that create member leads: one at a time, or an import. While the agreement is still
 * IN_NEGOTIATION the leads exist (D-L6) and the tab says why none of them can become a policy yet.
 *
 * A converted lead's row links to its policy, which is also a row of the Policies tab: a lead is
 * a lead until it is converted, then it is a policy AND a converted lead — never two names for one
 * object in two tabs.
 */
export function GroupPolicyLeadsTab({
  code,
  status,
}: {
  readonly code: string;
  /** The agreement's status — `IN_NEGOTIATION` disables Accept with a reason (§8.1.2). */
  readonly status: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  // The master's leads, paged like every list (SDD-017 §9); the filter is the tab's own.
  const [filter, setFilter] = useState<PropertyFilterValue>({ filters: '', search: '' });
  const [order, setOrder] = useState('');
  const catalog = useFilterCatalog('leads');
  const leads = useInfiniteList('leads', {
    filters: andFilters(`parentPolicyCode==${code}`, filter.filters),
    search: filter.search,
    order,
  });
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  // Over the LOADED leads; the sentence says so (`summaryLoaded`) while more pages exist.
  const counts = kindCounts(leads.items);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h3" component="h2">
          {t('leads.groupTab.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('leads.groupTab.description')}
        </Typography>
        {!leads.isLoading && !leads.isError && (
          <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="lead-kind-summary">
            {t(leads.hasNextPage ? 'leads.groupTab.summaryLoaded' : 'leads.groupTab.summary', {
              open: counts.STATUS_KIND_OPEN,
              accepted: counts.STATUS_KIND_ACCEPTED,
              converted: counts.STATUS_KIND_CONVERTED,
              closed: counts.STATUS_KIND_CLOSED,
            })}
          </Typography>
        )}
      </Box>

      {status === 'IN_NEGOTIATION' && (
        <Alert severity="info" data-testid="negotiating-notice">
          {t('leads.groupTab.negotiating')}
        </Alert>
      )}

      <RequirePermission anyOf={[p.leads.create]}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setCreating(true);
            }}
          >
            {t('leads.actions.newLead')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setImporting(true);
            }}
          >
            {t('leads.actions.import')}
          </Button>
        </Stack>
        <LeadFormDialog
          mode={{ kind: 'create', parentPolicyCode: code }}
          open={creating}
          onClose={() => {
            setCreating(false);
          }}
          onSaved={(leadCode) => {
            setCreating(false);
            void navigate({ href: links.insurance.lead(leadCode) });
          }}
        />
        <ImportLeadsWizard
          parentPolicyCode={code}
          open={importing}
          onClose={() => {
            setImporting(false);
            leads.refresh();
          }}
        />
      </RequirePermission>

      <PropertyFilter catalog={catalog.data} value={filter} onChange={setFilter} />
      <LeadGrid
        leads={leads.items}
        list={leads}
        emptyMessage={t('leads.groupTab.empty')}
        showParent={false}
        // The tab's parent goes as `parent`; the service adds it to the filter (SDD-020 §8.1).
        exportAs={{
          label: t('leads.groupTab.exportLabel', { code }),
          parent: code,
          filters: filter.filters,
          search: filter.search,
          order,
        }}
        order={order}
        onOrderChange={setOrder}
      />
    </Stack>
  );
}
